import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, classNames, Icon, List, TextInput } from '@pella/sash-framework';
import { useLazyQuery, useMutation } from '@apollo/client';
import { getLocalStorage, initDataLayer, removePhoneNumberFormat } from '../../../utilities/functions';
import LoadingOverlay from '../../../common/loading-overlay';
import PTIHCalendar from './calendar';
import PTIHTimeSlots from './calendar/time-slots';
import { initialCalendar } from './calendar/initial-calendar';
import { useAccount } from '../../../../hooks/use-account';
import { isValidEmail, isValidPhoneNumber } from '../../../utilities/validations';
import { CREATE_APPOINTMENT, GET_APPOINTMENT_CALENDAR } from '../../../../enums/graphql';
import PTIHSurvey from './survey';
import { captureException, getCurrentScope, setContext } from '@sentry/gatsby';
import { useOktaAuth } from '../../../../hooks/use-okta-auth';
import { useSelector } from 'react-redux';

const FifteenMinuteConsultation = ({ onConsultationStateChange, rep, view, canHaveAppt }) => {
	const [getCalendar, { data: calendarData, loading: calendarLoading, error: calendarError }] = useLazyQuery(GET_APPOINTMENT_CALENDAR);
	const locations = useSelector((state) => state.locations);
	const [complete, setComplete] = useState(false);
	const [errors, setErrors] = useState({});
	const { getUser } = useAccount();
	const { isLogged } = useOktaAuth();
	const token = getLocalStorage('okta-token-storage')?.accessToken?.accessToken || null;
	const [createAppointment, { data, loading: creatingAppointment, error }] = useMutation(CREATE_APPOINTMENT, {
		context: token ? { headers: { authorization: `Bearer ${token}` } } : undefined,
		onError: (error) => {
			setErrors((errors) => ({
				...errors,
				createAppointment: 'We are unable to process this appointment at this time.',
			}));
			getCurrentScope().setTransactionName('createAppointment');
			const { timezoneCode, timeslots, isInitial, startDate, endDate, selectedDay, selectedTimeslot, arrayStartPosition, timeslotLength } = { ...apiData, ...apptInfo };
			setContext('Error', {
				zip: consultData.zip,
				apiData: { timezoneCode, timeslots, isInitial },
				apptInfo: { startDate, endDate, selectedDay, selectedTimeslot, arrayStartPosition, timeslotLength },
			});
			captureException(error || 'Unknown Error Details');
		},

		onCompleted: (data) => {
			initDataLayer('ptih-form-submit');
			onConsultationStateChange({ data, status: 'SUCCESS' });
		},
	});

	const [consultData, setConsultData] = useState({
		firstName: '',
		email: '',
		zip: locations?.market?.postal_code,
		timeframe: '',
		installation: '',
		replacement_visualization: '',
		product_offering: '',
		financing_offers: '',
		phone: '',
	});

	const getUserRef = useRef(getUser);

	const loadProfile = useCallback(async () => {
		const fetchedUser = await getUserRef.current();
		if (fetchedUser?.data?.profile) {
			setConsultData((consultData) => ({
				...consultData,
				phone: fetchedUser?.data?.phoneNumber,
				firstName: fetchedUser?.data?.profile?.firstName,
				email: fetchedUser?.data?.profile?.email,
			}));
		}
	}, []);

	useEffect(() => {
		isLogged && loadProfile();
	}, [isLogged, loadProfile]);

	const [apiData, setApiData] = useState({
		initialCalendar: initialCalendar(),
		timeslots: [],
		isInitial: true,
		timezoneCode: '',
		timezoneShortCode: '',
	});

	const [apptInfo, setApptInfo] = useState({
		startDate: '',
		endDate: '',
		arrayStartPosition: null,
		timeslotLength: null,
		selectedDay: 0,
		selectedTimeslot: -1,
	});

	useEffect(() => {
		rep?.author_resource_id &&
			getCalendar({
				variables: {
					input: {
						resourceId: rep.author_resource_id,
						noOfDays: '14',
					},
				},
			});
	}, [getCalendar, rep?.author_resource_id]);

	useEffect(() => {
		if (!calendarLoading) {
			if (calendarData?.getAppointmentCalendar) {
				const { timeslots, timezoneCode, timezoneShortCode, resourceId } = calendarData.getAppointmentCalendar;
				if (timeslots.length) {
					const day = parseInt(timeslots[0].startDateTime.split('T')[0].split('-')[2]);
					const length = timeslots.filter((slot) => parseInt(slot.startDateTime.split('T')[0].split('-')[2]) === day).length;

					setApiData((apiData) => ({
						...apiData,
						timeslots,
						timezoneCode,
						timezoneShortCode,
						isInitial: false,
						resourceId,
					}));

					setApptInfo({
						selectedDay: day,
						arrayStartPosition: 0,
						timeslotLength: length,
						selectedTimeslot: 0,
						startDate: timeslots[0].startDateTime,
						endDate: timeslots[0].endDateTime,
					});
				} else {
					setApiData({
						initialCalendar: initialCalendar(),
						timeslots: [],
						isInitial: true,
						timezoneCode: '',
						timezoneShortCode: '',
					});
					setApptInfo({
						startDate: '',
						endDate: '',
						arrayStartPosition: null,
						timeslotLength: null,
						selectedDay: 0,
						selectedTimeslot: -1,
					});
				}
			} else if (calendarError) {
				setApiData({
					initialCalendar: initialCalendar(),
					timeslots: [],
					isInitial: true,
					timezoneCode: '',
					timezoneShortCode: '',
				});
				setApptInfo({
					startDate: '',
					endDate: '',
					arrayStartPosition: null,
					timeslotLength: null,
					selectedDay: 0,
					selectedTimeslot: -1,
				});
				setErrors((errors) => ({
					...errors,
					appointment: 'There was an error loading the calendar, please try again later.',
				}));
			}
		}
	}, [calendarLoading, calendarData, calendarError]);

	const handleDaySelect = (start, length, id) => {
		const newApptInfo = {
			arrayStartPosition: start,
			timeslotLength: length,
			selectedDay: id,
			selectedTimeslot: -1,
			startDate: '',
			endDate: '',
		};
		setApptInfo(newApptInfo);
		isFormComplete({ ...apiData, ...newApptInfo });
	};

	const handleTimeslotSelect = (timeslot, index) => {
		const newApptInfo = {
			startDate: timeslot.startDateTime,
			endDate: timeslot.endDateTime,
			selectedTimeslot: index,
		};
		setApptInfo({ ...apptInfo, ...newApptInfo });
		isFormComplete({ ...apiData, ...newApptInfo });
	};

	const isFormComplete = (formState) => {
		const isComplete = !!(formState.startDate && formState.endDate && consultData.firstName && consultData.phone && consultData.email);
		if (isComplete !== complete) {
			setErrors({});
		}
		setComplete(isComplete);
	};

	const validateInputs = (inputs, apptInfo) => {
		const { firstName, phone } = inputs;
		const { startDate, endDate } = apptInfo;
		const updatedErrors = {};
		if (!firstName) updatedErrors.firstName = 'First name is required';
		if (!phone || !isValidPhoneNumber(phone)) updatedErrors.phone = 'Phone number is required';
		if (view === 'rta' && (!inputs.email || !isValidEmail(inputs.email))) updatedErrors.email = 'Email is required';
		if (!startDate || !endDate) updatedErrors.appointment = 'Date and time are required';
		return updatedErrors;
	};

	const handleCreateAppointment = async (e) => {
		e.preventDefault();
		setErrors({});
		const validationErrors = validateInputs(consultData, apptInfo);
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			return;
		}

		const surveyResults = `Survey: Timeframe 6-12 months: ${consultData.timeframe}, Professional Installation Required: ${consultData.installation}, Replacement Visualization: ${consultData.replacement_visualization ? 'Yes' : 'No'}, Product Offering: ${consultData.product_offering ? 'Yes' : 'No'}, Financing Offers: ${consultData.financing_offers ? 'Yes' : 'No'}`;

		try {
			await createAppointment({
				variables: {
					input: {
						appointment: {
							customerDetails: surveyResults,
							email: consultData.email,
							endDate: apptInfo.endDate,
							firstName: consultData.firstName,
							phoneNumber: removePhoneNumberFormat(consultData.phone),
							postalCode: consultData.zip,
							resourceId: apiData.resourceId,
							startDate: apptInfo.startDate,
							timezoneCode: apiData.timezoneCode,
						},
					},
				},
			});
		} catch (error) {
			// eslint-disable-next-line no-console -- temporary
			console.error('Failed to create appointment:', error);
		}
	};

	const inputChange = (e) => {
		const { name, type, checked, value } = e.target;
		setConsultData((consultData) => ({
			...consultData,
			[name]: type === 'checkbox' ? checked : value,
		}));
	};

	const customerDetailsFormSubHeading = classNames({
		'text-body-2 text-black mt-0': true,
		'mb-5 md:mb-4': view === 'projects',
		'mb-4 md:mb-3 xl:mb-4': view === 'rta',
	});

	const customerDetailsInputContainerClasses = classNames({
		'grid-cols-2 md:grid md:gap-x-4': true,
		'xl:grid-cols-3': !isLogged,
		'md:!gap-x-10': isLogged,
	});

	return (
		<div>
			<hr className='mb-6 mt-2 border-t border-gray-15 md:mb-8 md:mt-5' />
			<div>
				<p className='text-h3 mb-6'>Select a Date and Time</p>

				<div className='mb-10 flex flex-col md:px-20 xl:flex-row xl:px-0'>
					{(calendarLoading || creatingAppointment) && <LoadingOverlay fixed={false} />}
					{(apiData.isInitial || apiData.timeslots.length > 0) && (
						<PTIHCalendar
							timeslots={apiData.isInitial || !rep ? apiData.initialCalendar : apiData.timeslots}
							handleSelect={handleDaySelect}
							selectedDay={apiData.isInitial || !rep ? new Date().getDate() : apptInfo.selectedDay}
							disabled={apiData.isInitial || !rep}
						/>
					)}

					<hr className='mb-8 mt-6 md:mb-12 md:mt-10 xl:hidden' />

					<div className='w-full xl:ml-10'>
						<p className='text-body-2 mb-1 flex items-center text-gray-60'>
							<Icon
								name='schedule'
								size={16}
								className='m-2'
							/>
							All times in {apiData.timezoneShortCode}
						</p>

						{apiData.isInitial || (apiData.timeslots.length > 0 && apptInfo.arrayStartPosition != null && apptInfo.timeslotLength != null) ? (
							<PTIHTimeSlots
								timeslots={apiData.isInitial || !rep ? apiData.initialCalendar : apiData.timeslots}
								arrayStartPosition={apiData.isInitial || !rep ? 0 : apptInfo.arrayStartPosition}
								selectedIndex={apiData.isInitial || !rep ? 0 : apptInfo.selectedTimeslot}
								timeslotLength={apiData.isInitial || !rep ? 3 : apptInfo.timeslotLength}
								handleSelect={handleTimeslotSelect}
								disabled={calendarLoading || apiData.isInitial || !rep}
								initial={apiData.isInitial}
							/>
						) : (
							<PTIHTimeSlots
								timeslots={apiData.isInitial || !rep ? apiData.initialCalendar : apiData.timeslots}
								arrayStartPosition={0}
								selectedIndex={-1}
								timeslotLength={0}
								handleSelect={handleTimeslotSelect}
								disabled={calendarLoading || apiData.isInitial || !rep}
								initial={apiData.isInitial}
							/>
						)}
					</div>
				</div>
			</div>

			<div>
				<p className='text-h3 mb-2'>Provide Contact Information</p>
				<p className={customerDetailsFormSubHeading}>Enter your preferred contact information so we know where to reach you.</p>

				<form
					noValidate
					onSubmit={handleCreateAppointment}
				>
					<div
						id='contactInfo'
						className='mb-3'
					>
						<div className={customerDetailsInputContainerClasses}>
							<TextInput
								id='firstName'
								labelText='First Name'
								onChange={inputChange}
								value={consultData.firstName}
								required
								errorText={errors.firstName}
								error={!!errors.firstName}
								autoComplete='given-name'
							/>

							<TextInput
								type='tel'
								id='phone'
								labelText='Phone Number'
								onChange={inputChange}
								value={consultData.phone}
								required
								errorText={errors.phone}
								error={!!errors.phone}
								format='(###) ###-####'
								allowEmptyFormatting
								autoComplete='on'
							/>

							{!isLogged && (
								<TextInput
									type='email'
									id='email'
									labelText='Email'
									onChange={inputChange}
									value={consultData.email}
									required
									errorText={errors.email}
									error={!!errors.email}
									autoComplete='email'
									disabled={isLogged}
								/>
							)}
						</div>
					</div>

					<PTIHSurvey
						inputChange={inputChange}
						rep={rep}
						canHaveAppt={canHaveAppt}
					/>

					{Object.keys(errors).length > 0 && (
						<Alert
							type='error'
							aria-live='assertive'
							className='mb-4'
							hideClose
						>
							<p>Please correct the following errors:</p>
							<List className='list-inside pl-0'>
								{Object.values(errors).map((err, index) => {
									if (!err) return null;
									return (
										<li
											key={index}
											dangerouslySetInnerHTML={{
												__html: err,
											}}
										/>
									);
								})}
							</List>
						</Alert>
					)}

					<div className='flex sm:justify-end'>
						<Button
							color='primary-alt'
							type='submit'
							className='sm:!w-fit'
							disabled={!canHaveAppt}
							size='lg'
						>
							Submit
						</Button>
					</div>
				</form>
			</div>
		</div>
	);
};

FifteenMinuteConsultation.propTypes = {
	onConsultationStateChange: PropTypes.func.isRequired,
	rep: PropTypes.shape({
		author_resource_id: PropTypes.string.isRequired,
	}).isRequired,
	view: PropTypes.string,
	canHaveAppt: PropTypes.bool.isRequired,
};

export default FifteenMinuteConsultation;
